import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const AlertContext = createContext(null);
const ConfirmContext = createContext(null);

function getAlertType(message) {
  return /hiba|sikertelen|nem egyezik|kötelező|figyelem|nem törölhetsz|foglalni|között/i.test(message)
    ? 'error'
    : 'success';
}

export function AlertProvider({ children }) {
  const [alert, setAlert] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const confirmResolver = useRef(null);

  const showAlert = useCallback((message, type) => {
    setAlert({ message: String(message), type: type || getAlertType(String(message)) });
  }, []);

  const closeAlert = useCallback(() => setAlert(null), []);

  const confirm = useCallback((message) => new Promise((resolve) => {
    confirmResolver.current = resolve;
    setConfirmation(String(message));
  }), []);

  const closeConfirmation = useCallback((result) => {
    confirmResolver.current?.(result);
    confirmResolver.current = null;
    setConfirmation(null);
  }, []);

  useEffect(() => {
    if (!alert) return undefined;
    const timeout = window.setTimeout(closeAlert, 4500);
    return () => window.clearTimeout(timeout);
  }, [alert, closeAlert]);

  return (
    <AlertContext.Provider value={showAlert}>
      <ConfirmContext.Provider value={confirm}>
        {children}
        {alert && (
        <div className="fixed inset-x-4 top-6 z-[200] flex justify-center pointer-events-none sm:inset-x-auto sm:right-6 sm:left-auto sm:w-full sm:max-w-md">
          <div
            role="alert"
            aria-live="assertive"
            className={`pointer-events-auto w-full rounded-2xl border bg-white/95 p-5 shadow-2xl backdrop-blur-xl dark:bg-gray-800/95 ${
              alert.type === 'error'
                ? 'border-rose-200 dark:border-rose-800'
                : 'border-emerald-200 dark:border-emerald-800'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl ${
                alert.type === 'error'
                  ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/50 dark:text-rose-300'
                  : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/50 dark:text-emerald-300'
              }`}>
                {alert.type === 'error' ? '!' : '✓'}
              </div>
              <p className="flex-1 whitespace-pre-line pt-1 text-sm font-bold leading-6 text-gray-700 dark:text-gray-200">
                {alert.message}
              </p>
              <button
                type="button"
                onClick={closeAlert}
                aria-label="Értesítés bezárása"
                className="text-xl leading-none text-gray-400 transition-colors hover:text-gray-700 dark:text-gray-500 dark:hover:text-white"
              >
                ×
              </button>
            </div>
          </div>
        </div>
        )}
        {confirmation && (
          <div className="fixed inset-0 z-[210] flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirmation-title"
              className="w-full max-w-md rounded-[1rem] border border-white/20 bg-white p-7 shadow-2xl dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100 text-2xl text-orange-600 dark:bg-orange-900/50 dark:text-orange-300">
                ?
              </div>
              <h2 id="confirmation-title" className="mb-3 text-xl font-black text-gray-900 dark:text-white">
                Biztosan folytatod?
              </h2>
              <p className="whitespace-pre-line text-sm font-semibold leading-6 text-gray-600 dark:text-gray-300">
                {confirmation}
              </p>
              <div className="mt-7 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => closeConfirmation(false)}
                  className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-black text-gray-600 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                  Mégse
                </button>
                <button
                  type="button"
                  onClick={() => closeConfirmation(true)}
                  className="rounded-xl bg-red-600 px-5 py-3 text-sm font-black text-white shadow-lg transition-colors hover:bg-red-700"
                >
                  Igen, folytatom
                </button>
              </div>
            </div>
          </div>
        )}
      </ConfirmContext.Provider>
    </AlertContext.Provider>
  );
}

export function useAlert() {
  const showAlert = useContext(AlertContext);
  if (!showAlert) throw new Error('useAlert must be used inside AlertProvider');
  return showAlert;
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside AlertProvider');
  return confirm;
}

export function useDoubleConfirm() {
  const confirm = useConfirm();

  return useCallback(async (message, secondMessage) => {
    if (!await confirm(message)) return false;
    return confirm(secondMessage || 'Ez a művelet végleges. Biztosan folytatod?');
  }, [confirm]);
}